/**
 * CategoryBadge — Decorative pill for taxonomy / type / category labels.
 *
 * Sibling to <StatusBadge> but with a wider palette intended for non-severity
 * labels (template categories, form types, transaction types, etc.). The
 * 13-tone palette covers every existing CATEGORY_COLORS / TYPE_COLORS
 * hand-rolled table found across the email, reports, and finance apps as of
 * 2026-04-26 (cross-app inventory in
 * `agents/handoffs/email_template_category_meta_handoff_2026-04-26.md`).
 *
 * @layer atom
 * @package @plerity/ui-core
 */
import { memo } from "react";
import { cn } from "../utils/cn";
import type { CategoryBadgeProps, CategoryBadgeSize, CategoryTone } from "./CategoryBadge.types";

/**
 * Tone → Tailwind class map. All entries follow the same shape:
 *   bg-{palette}-100 text-{palette}-700 dark:bg-{palette}-900/30 dark:text-{palette}-300
 *
 * - `gray`  uses the literal gray ramp with a `dark:` pair (the web gray ramp is STATIC
 *           under html.dark; only the mobile-only colors.css block inverts).
 * - `blue`  uses the semantic `info` scale rather than raw `blue-*` so it
 *           inherits any future runtime brand override.
 *
 * Verified against tokens-apps/src/colors.css — every class resolves to a
 * declared CSS var or a Tailwind v4 standard scale (no compile-time misses).
 */
const TONE_CLASSES: Record<CategoryTone, string> = {
  // gray: literal ramp WITH a dark: pair. Every SPA (and the branded portal) pins a
  // STATIC gray ramp under html.dark, so a bare `bg-gray-100` rendered a light pill
  // on dark cards. Dark uses gray-600 (not 700) so the pill stays visible on gray-700
  // rows / hovered table rows: #475569 / #f1f5f9 = 6.9:1; light #f1f5f9 / #334155 = 9.4:1.
  gray: "bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-100",
  blue: "bg-info-100 text-info-700 dark:bg-info-900/30 dark:text-info-300",
  green: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  amber: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  red: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  purple: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  pink: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300",
  teal: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
  indigo: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
  emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  violet: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
  orange: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  cyan: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
};

const SIZE_CLASSES: Record<CategoryBadgeSize, string> = {
  xs: "text-xs px-2.5 py-0.5 gap-1.5",
  "3xs": "text-3xs px-2 py-0.5 gap-1",
};

const VALID_TONES = new Set<CategoryTone>([
  "gray",
  "blue",
  "green",
  "amber",
  "red",
  "purple",
  "pink",
  "teal",
  "indigo",
  "emerald",
  "violet",
  "orange",
  "cyan",
]);

function resolveTone(tone: string | undefined): CategoryTone {
  if (tone && VALID_TONES.has(tone as CategoryTone)) return tone as CategoryTone;
  return "gray";
}

export const CategoryBadge = memo(function CategoryBadge({
  label,
  tone,
  tonesMap,
  size = "xs",
  leadingIcon,
  className,
}: CategoryBadgeProps) {
  let effectiveTone: CategoryTone;
  let displayLabel: string;

  if (tonesMap && tone && tone in tonesMap) {
    const config = tonesMap[tone];
    effectiveTone = resolveTone(config.tone);
    displayLabel = label ?? config.label;
  } else if (tonesMap) {
    // tonesMap provided but key missing — fall back to neutral with raw tone string as label
    effectiveTone = "gray";
    displayLabel = label ?? tone ?? "";
  } else {
    effectiveTone = resolveTone(tone);
    displayLabel = label ?? tone ?? "";
  }

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full font-medium",
        SIZE_CLASSES[size],
        TONE_CLASSES[effectiveTone],
        className
      )}
    >
      {leadingIcon}
      {displayLabel}
    </span>
  );
});

CategoryBadge.displayName = "CategoryBadge";
